import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import flow from 'lodash/fp/flow';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, CustomEditorModule, RowApiModule } from 'ag-grid-community';
import type {
  CellEditingStoppedEvent,
  IRowNode,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  StoreRefreshedEvent,
} from 'ag-grid-community';
import { MasterDetailModule, ServerSideRowModelModule } from 'ag-grid-enterprise';

// Editing modules: CustomEditorModule (withholdingTax + validUntil custom editors),
// RowApiModule (node.setDataValue rollback).
ModuleRegistry.registerModules([ServerSideRowModelModule, MasterDetailModule, CustomEditorModule, RowApiModule]);

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';

import useDebounceValue from 'hooks/useDebounceValue';

import { fetchBeneficiariesGrid, useCreateBeneficiaryMutation, useUpdateVendorFieldMutation, beneficiaryFormQuery } from 'domain/payments/queries';
import { queryClient } from 'services/query';
import { payModuleAccountIdSelector } from 'domain/env';
import { yieldSuccessNotification } from 'lib/toasts';

import useAgGridProps from 'components/AgGrid/Beneficiaries/useAgGridProps';
import useColumnTypes from 'components/AgGrid/Beneficiaries/useAgGridColumnTypes';
import useFetchColumnDefs from 'components/AgGrid/Beneficiaries/useFetchColumnDefs';
import useAgGridTranslations from 'components/AgGrid/hooks/useAgGridTranslations';
import useAgGridTheme from 'components/AgGrid/hooks/useAgGridTheme';
import {
  addTooltipPropsForColDefs,
  addHeaderTooltipPropsForColDefs,
} from 'components/AgGrid/helpers/headerTooltipHelpers';
import { filterMapping } from 'components/AgGrid/Beneficiaries/filterHelpers';
import { addFilters } from 'components/AgGrid/helpers/filterHelpers';
import { setExelModeFilter, GRID_FILTER_BUTTONS } from 'components/AgGrid/helpers';

import AgGridCustomTooltip from 'components/AgGrid/components/CustomTooltip';
import AgGridColumnHeader from 'components/AgGrid/components/ColumnHeader';
import CustomSetColumnFilter from 'components/AgGrid/components/CustomHeaderFilter/CustomSetColumnFilter/CustomSetColumnFilter';
import AgGridGlobalStyles from 'components/AgGrid/components/AgGridGlobalStyles';
import AgGridStyles from 'components/AgGrid/Beneficiaries/AgGridStyles';
import BeneficiaryDetailCellRenderer from './components/BeneficiaryDetailCellRenderer';
import AccountsCountChipRenderer from './components/AccountsCountChipRenderer';
import VendorActionsRenderer, { AddVendorPayload } from './components/VendorActionsRenderer';
import NoRowsOverlay from './components/NoRowsOverlay';
import useFetchBeneficiariesTotal from './useFetchBeneficiariesTotal';
import ValidUntilDateCellEditor from 'components/AgGrid/Beneficiaries/components/ValidUntilDateCellEditor';
import WithholdingTaxCellEditor from 'components/AgGrid/Beneficiaries/components/WithholdingTaxCellEditor';
import VendorFieldCellRenderer from 'components/AgGrid/Beneficiaries/components/VendorFieldCellRenderer';

import BeneficiaryModal from 'pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal';

const EXPAND_COLUMN_DEF = {
  field: '__expand__',
  cellRenderer: 'agGroupCellRenderer',
  lockPinned: true,
  maxWidth: 44,
  minWidth: 44,
  filter: false,
  sortable: false,
  resizable: false,
  suppressColumnsToolPanel: true,
  suppressFiltersToolPanel: true,
  suppressNavigable: true,
  suppressMovable: true,
  hide: false,
  cellClass: 'beneficiaries-service-cell',
  cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0px' },
  headerComponentParams: { suppressHeaderRenderer: true },
};

// Pin the master-detail expander to the leading edge — right in RTL, left in LTR
// (mirrors selectionColumnDef in useAgGridProps).
const addExpandColumn = (enableRtl: boolean) => (cols: unknown[]) => {
  const side = enableRtl ? 'right' : 'left';
  return [{ ...EXPAND_COLUMN_DEF, pinned: side, lockPosition: side }, ...cols];
};

const ACTIONS_COLUMN_DEF = {
  field: '__actions__',
  cellRenderer: 'vendorActionsRenderer',
  maxWidth: 130,
  minWidth: 130,
  filter: false,
  sortable: false,
  resizable: false,
  suppressColumnsToolPanel: true,
  suppressFiltersToolPanel: true,
  suppressNavigable: true,
  suppressMovable: true,
  lockPinned: true,
  hide: false,
  cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' },
  headerComponentParams: { suppressHeaderRenderer: true },
};

const addActionsColumn = (cols: unknown[]) => [...cols, ACTIONS_COLUMN_DEF];

const addChipRenderer = (cols: unknown[]) =>
  (cols as Array<Record<string, unknown>>).map((col) =>
    col.field === 'beneficiariesQty' ? { ...col, cellRenderer: 'accountsCountChipRenderer' } : col,
  );

type AddModalVendor = AddVendorPayload;

const Beneficiaries: React.FC = () => {
  const { formatMessage } = useIntl();
  const agGridDefaultProps = useAgGridProps();
  const { getLocaleText } = useAgGridTranslations();
  const agGridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridRef = useRef<AgGridReact>(null);
  const accountId = useSelector(payModuleAccountIdSelector);

  useEffect(() => {
    if (accountId) queryClient.prefetchQuery(beneficiaryFormQuery(accountId));
  }, [accountId]);
  const [perPage, setPerPage] = useState(agGridDefaultProps.paginationPageSize);
  const [addModalVendor, setAddModalVendor] = useState<AddModalVendor | null>(null);
  const handleVendorAdd = useCallback((vendor: AddVendorPayload) => setAddModalVendor(vendor), []);

  const [allExpanded, setAllExpanded] = useState(false);
  const [hasRows, setHasRows] = useState(false);

  // Derive toolbar state from the grid itself (not a button-only flag) so that manual
  // expand/collapse of detail rows — and an empty result set — keep the button label and its
  // disabled state in sync. Wired to onModelUpdated, which fires on data loads, page changes,
  // search and every expand/collapse alike.
  const syncToolbarState = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      setAllExpanded(false);
      setHasRows(false);
      return;
    }
    const masterNodes: IRowNode[] = [];
    api.forEachNode((node) => {
      // In master-detail mode forEachNode also visits generated detail nodes (same data) —
      // restrict to master rows so detail nodes don't skew the count / expanded check.
      if (node.master && node.data?.vendorId) masterNodes.push(node);
    });
    setHasRows(masterNodes.length > 0);
    setAllExpanded(masterNodes.length > 0 && masterNodes.every((node) => node.expanded));
  }, []);

  // Toggle: open all loaded master rows when not fully expanded, otherwise collapse them all.
  const handleToggleExpandAll = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const shouldExpand = !allExpanded;
    api.forEachNode((node) => {
      // Only master rows are expandable; skip generated detail nodes (same data).
      if (node.master && node.data?.vendorId) node.setExpanded(shouldExpand);
    });
  }, [allExpanded]);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounceValue(searchInput, 300);
  const searchTermRef = useRef('');

  const updateVendorField = useUpdateVendorFieldMutation();
  const createBeneficiary = useCreateBeneficiaryMutation();

  const { beneficiariesTotal, beneficiariesTotalIsFetched, fetchGridTotal } = useFetchBeneficiariesTotal();

  // Keep the latest unfiltered total in a ref so the no-rows overlay (rendered by AG Grid
  // outside the React render cycle) can read it without re-creating the grid context.
  const totalRef = useRef(beneficiariesTotal ?? 0);
  totalRef.current = beneficiariesTotal ?? 0;

  // In-flight PATCH count per cell (vendorId:field) — drives the in-cell spinner via the grid
  // context. A counter (not a boolean set) so a still-pending re-edit of the same cell keeps the
  // spinner until the last request settles. A ref so updating it never re-creates the context.
  const updatingCellsRef = useRef<Map<string, number>>(new Map());

  const gridContext = useMemo(
    () => ({
      getHasAnyRecords: () => totalRef.current > 0,
      isCellUpdating: (vendorId?: string, field?: string) =>
        !!vendorId && !!field && (updatingCellsRef.current.get(`${vendorId}:${field}`) ?? 0) > 0,
    }),
    [],
  );

  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    searchTermRef.current = debouncedSearch;
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [debouncedSearch]);

  const { vendorColumnDefs, beneficiaryColumnDefs, rawColumnDefsIsBusy } = useFetchColumnDefs();

  const columnDefs = useMemo(
    // addFilters redefines 'headerComponentParams', functions modifying it must appear further in composition
    () =>
      flow(
        addTooltipPropsForColDefs,
        addFilters(filterMapping),
        addHeaderTooltipPropsForColDefs,
        setExelModeFilter('windows', [GRID_FILTER_BUTTONS.APPLY, GRID_FILTER_BUTTONS.RESET]),
        addChipRenderer,
        addExpandColumn(agGridDefaultProps.enableRtl),
        addActionsColumn,
      )(vendorColumnDefs),
    [vendorColumnDefs, agGridDefaultProps.enableRtl],
  );

  const pendingExpandRef = useRef<Set<string> | null>(null);

  const refreshGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    // The unfiltered total can change after an add/delete — keep it in sync.
    fetchGridTotal();
    const expandedIds = new Set<string>();
    api.forEachNode((node) => {
      if (node.expanded && node.data?.vendorId) expandedIds.add(node.data.vendorId);
    });
    pendingExpandRef.current = expandedIds.size > 0 ? expandedIds : null;
    api.refreshServerSide({ purge: true });
  }, [fetchGridTotal]);

  const softRefreshGrid = refreshGrid;

  const onStoreRefreshed = useCallback((_e: StoreRefreshedEvent) => {
    const toExpand = pendingExpandRef.current;
    if (!toExpand?.size) return;
    pendingExpandRef.current = null;
    requestAnimationFrame(() => {
      toExpand.forEach((vendorId) => {
        const node = gridRef.current?.api?.getRowNode(vendorId);
        if (node) node.setExpanded(true);
      });
    });
  }, []);

  const serverDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        const { success, request, api } = params;
        const { startRow, endRow, filterModel, sortModel } = request;

        api.hideOverlay();

        const requestData = {
          filterModel,
          sortModel,
          paginationModel: {
            limit: (endRow ?? 0) - (startRow ?? 0),
            offset: startRow ?? 0,
          },
          ...(searchTermRef.current ? { searchTerm: searchTermRef.current } : {}),
        };

        try {
          const res = await fetchBeneficiariesGrid(requestData);

          success({ rowData: res.items, rowCount: res.count });

          // Master detail rows are open by default: expand every freshly loaded master row.
          // This runs only on data loads (initial / pagination / search / refresh), never on a
          // manual expand-collapse, so it won't re-expand a row the user has just collapsed.
          requestAnimationFrame(() => {
            (res.items as Array<{ vendorId?: string }>).forEach((item) => {
              const node = item?.vendorId ? api.getRowNode(item.vendorId) : undefined;
              if (node && !node.expanded) node.setExpanded(true);
            });
          });

          if (res.count === 0) api.showNoRowsOverlay();
        } catch (error) {
          console.log('[ERROR]: on fetch grid rows', error);
          success({ rowData: [], rowCount: 0 });
          api.showNoRowsOverlay();
        }
      },
    }),
    [],
  );

  const components = useMemo(
    () => ({
      customTooltip: AgGridCustomTooltip,
      agColumnHeader: AgGridColumnHeader,
      customSetColumnFilter: CustomSetColumnFilter,
      accountsCountChipRenderer: AccountsCountChipRenderer,
      validUntilDateCellEditor: ValidUntilDateCellEditor,
      withholdingTaxCellEditor: WithholdingTaxCellEditor,
      vendorFieldCellRenderer: VendorFieldCellRenderer,
      beneficiaryDetailCellRenderer: (props: { data: unknown }) => (
        <BeneficiaryDetailCellRenderer
          data={props.data as any}
          beneficiaryHeaders={beneficiaryColumnDefs}
          onSoftRefresh={softRefreshGrid}
        />
      ),
      vendorActionsRenderer: (props: { data: Record<string, unknown> }) => (
        <VendorActionsRenderer data={props.data} onAdd={handleVendorAdd} />
      ),
    }),
    [softRefreshGrid, handleVendorAdd, beneficiaryColumnDefs],
  );

  const onPaginationChanged = useCallback(({ newPageSize, api }: PaginationChangedEvent) => {
    if (newPageSize) {
      const size = api.paginationGetPageSize();
      setPerPage(size);
    }
  }, []);

  const handleAddSubmit = useCallback(
    (data: Record<string, unknown>) =>
      new Promise<void>((resolve, reject) => {
        const { details, vendorId, isDefault, source } = data as {
          details: Record<string, string | undefined>;
          vendorId: string;
          isDefault: boolean;
          source: 'erp' | 'user';
        };
        createBeneficiary.mutate(
          { body: { details, vendor_id: vendorId, is_default: isDefault, source } },
          {
            onSuccess: () => {
              refreshGrid();
              yieldSuccessNotification(
                formatMessage({
                  id: 'payment.toasts.beneficiary.create.success',
                  defaultMessage: 'Beneficiary created',
                }),
              );
              resolve();
            },
            // Reject (not resolve-on-settled) so the modal stays open and keeps the
            // user's input on failure, e.g. a duplicate-beneficiary 400. The error
            // toast comes from the mutation hook's onError.
            onError: (err) => reject(err),
          },
        );
      }),
    [createBeneficiary, refreshGrid, formatMessage],
  );

  // Commit an inline edit (Withholding Tax / Valid Until) on blur or Enter -> PATCH by vendorId.
  const onCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent) => {
      const { node, column, data, oldValue, valueChanged, api } = event;
      if (!valueChanged) return;

      // Key everything off the column's data `field` (not colId) so it stays consistent with the
      // renderer's spinner lookup and the `data[field]` read; pass the Column object to grid APIs
      // to avoid any colId/field ambiguity.
      const field = column.getColDef().field;
      const vendorId = data?.vendorId as string | undefined;
      if (!field || !vendorId) return;
      const value = data[field];

      // Optimistically reflect the edit in the grid immediately, then persist via PATCH.
      api.applyServerSideTransaction({ update: [data] });

      // Show an in-cell spinner while the PATCH is in flight (counted, to survive concurrent re-edits).
      const cellKey = `${vendorId}:${field}`;
      const cells = updatingCellsRef.current;
      const refreshCell = () => api.refreshCells({ rowNodes: [node], columns: [column], force: true });
      cells.set(cellKey, (cells.get(cellKey) ?? 0) + 1);
      refreshCell();

      updateVendorField.mutate(
        { path: { vendorId }, body: { [field]: value } },
        {
          onSuccess: (data) => {
            api.refreshCells({ rowNodes: [node], columns: [column], force: true });
            yieldSuccessNotification(
              formatMessage(
                {
                  id: 'payment.toasts.beneficiary.vendorField.update.success',
                  defaultMessage: 'Updated. {count, plural, one {# payment updated} other {# payments updated}}',
                },
                { count: data?.updatedPaymentsCount ?? 0 },
              ),
            );
          },
          onSettled: () => {
            const pending = (cells.get(cellKey) ?? 1) - 1;
            if (pending > 0) cells.set(cellKey, pending);
            else cells.delete(cellKey);
            refreshCell();
          },
          onError: () => {
            node.setDataValue(column, oldValue);
          },
        },
      );
    },
    [updateVendorField, formatMessage],
  );

  return (
    <Box className="ag-theme-dokka" display="flex" flexDirection="column" height="100%">
      <AgGridGlobalStyles />
      <AgGridStyles />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} px={1}>
        <Typography variant="subtitle1" fontWeight={500}>
          {formatMessage({
            id: 'configurations.company.beneficiaries.title',
            defaultMessage: 'Beneficiaries',
          })}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            onClick={handleToggleExpandAll}
            disabled={!hasRows}
            variant="outlined"
            size="small"
            startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {allExpanded
              ? formatMessage({
                  id: 'configurations.company.beneficiaries.collapseAll',
                  defaultMessage: 'Collapse all',
                })
              : formatMessage({
                  id: 'configurations.company.beneficiaries.expandAll',
                  defaultMessage: 'Expand all',
                })}
          </Button>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={formatMessage({
              id: 'configurations.company.beneficiaries.search.placeholder',
              defaultMessage: 'Search vendors...',
            })}
            sx={{ width: 320 }}
            slotProps={{
              input: {
                sx: { height: 40 },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Stack>

      {(rawColumnDefsIsBusy || !beneficiariesTotalIsFetched) && <LinearProgress />}
      {!rawColumnDefsIsBusy && beneficiariesTotalIsFetched && (
        <AgGridReact
          {...(agGridDefaultProps as any)}
          ref={gridRef}
          theme={agGridTheme}
          serverSideDatasource={serverDatasource}
          columnTypes={columnTypes}
          columnDefs={columnDefs}
          components={components}
          context={gridContext}
          noRowsOverlayComponent={NoRowsOverlay}
          serverSideInitialRowCount={beneficiariesTotal || 1}
          onPaginationChanged={onPaginationChanged}
          onStoreRefreshed={onStoreRefreshed}
          onModelUpdated={syncToolbarState}
          onCellEditingStopped={onCellEditingStopped}
          cacheBlockSize={perPage}
          getLocaleText={getLocaleText}
          getRowId={(params: { data: Record<string, string> }) => params.data.vendorId}
        />
      )}

      {addModalVendor && (
        <BeneficiaryModal
          onClose={() => setAddModalVendor(null)}
          onSubmit={handleAddSubmit}
          vendorId={addModalVendor.vendorId}
          vendorName={addModalVendor.vendorName}
          existingAccountsCount={addModalVendor.accountsCount}
        />
      )}
    </Box>
  );
};

export default Beneficiaries;
